import { useState } from 'react';
import type { MouseEvent, ReactElement } from 'react';
import { Button, Input, Modal, cn } from '@xdocs/ui';
import { useEditorT } from './useEditorT.js';
import styles from './EditorToolbar.module.css';

/**
 * Mockup (21-edit-doc) renders formatting commands as short text glyphs
 * (B / I / H / ☰ / ❝ / 🔗) in 32×32 squares rather than an icon set — matched
 * here via `styles['glyphButton']`. The accessible name still comes from
 * `t(...)` via `aria-label`/`title`, so this is a purely visual swap.
 */

export interface EditorToolbarProps {
  disabled?: boolean;
  className?: string | undefined;
}

/**
 * F67 WYSIWYG formatting toolbar (headings, body styles, lists, bold/
 * italic, links, block quotes — FEATURES.md F67 "Main components"). No
 * rich-text library is on the E13 approved-libs allowlist, so this is a
 * `document.execCommand`-based command bar over whichever contenteditable
 * region currently holds focus/selection (`ManuscriptEditor`'s title/body
 * regions) — the task-specified "contenteditable-based with document
 * structure model" fallback. `execCommand` is deprecated-but-universally-
 * supported for this exact basic-formatting surface across evergreen
 * browsers; flagged as an open issue for a dedicated editor lib via the
 * §13.2 new-dep procedure once one is approved.
 *
 * Each button `onMouseDown`-preventsDefault so clicking it never steals
 * focus/selection away from the editable region `execCommand` targets —
 * without this, every command would silently no-op.
 */
export function EditorToolbar({ disabled = false, className }: EditorToolbarProps): ReactElement {
  const { t } = useEditorT();
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  function preventFocusLoss(event: MouseEvent<HTMLButtonElement>): void {
    event.preventDefault();
  }

  function exec(command: string, value?: string): void {
    document.execCommand(command, false, value);
  }

  function confirmLink(): void {
    const url = linkUrl.trim();
    if (url) exec('createLink', url);
    setLinkModalOpen(false);
    setLinkUrl('');
  }

  const commands: Array<{ id: string; label: string; glyph: string; glyphClassName?: string | undefined; onRun: () => void }> = [
    { id: 'bold', label: t('editor:toolbar.bold'), glyph: 'B', glyphClassName: styles['glyphBold'], onRun: () => exec('bold') },
    { id: 'italic', label: t('editor:toolbar.italic'), glyph: 'I', glyphClassName: styles['glyphItalic'], onRun: () => exec('italic') },
    { id: 'heading', label: t('editor:toolbar.heading'), glyph: 'H', onRun: () => exec('formatBlock', 'h2') },
  ];
  const listCommands: Array<{ id: string; label: string; glyph: string; onRun: () => void }> = [
    { id: 'bulletList', label: t('editor:toolbar.bulletList'), glyph: '☰', onRun: () => exec('insertUnorderedList') },
    { id: 'numberedList', label: t('editor:toolbar.numberedList'), glyph: '≡', onRun: () => exec('insertOrderedList') },
    { id: 'blockquote', label: t('editor:toolbar.blockquote'), glyph: '❝', onRun: () => exec('formatBlock', 'blockquote') },
  ];

  return (
    <div className={cn(styles['toolbar'], className)} role="toolbar" aria-label={t('editor:toolbar.label')}>
      {commands.map((cmd) => (
        <Button
          key={cmd.id}
          size="icon"
          variant="ghost"
          className={styles['glyphButton']}
          aria-label={cmd.label}
          title={cmd.label}
          disabled={disabled}
          onMouseDown={preventFocusLoss}
          onClick={cmd.onRun}
        >
          <span className={cmd.glyphClassName} aria-hidden="true">
            {cmd.glyph}
          </span>
        </Button>
      ))}
      <span className={styles['divider']} aria-hidden="true" />
      {listCommands.map((cmd) => (
        <Button
          key={cmd.id}
          size="icon"
          variant="ghost"
          className={styles['glyphButton']}
          aria-label={cmd.label}
          title={cmd.label}
          disabled={disabled}
          onMouseDown={preventFocusLoss}
          onClick={cmd.onRun}
        >
          <span aria-hidden="true">{cmd.glyph}</span>
        </Button>
      ))}
      <span className={styles['divider']} aria-hidden="true" />
      <Button
        size="icon"
        variant="ghost"
        className={styles['glyphButton']}
        aria-label={t('editor:toolbar.link')}
        title={t('editor:toolbar.link')}
        disabled={disabled}
        onMouseDown={preventFocusLoss}
        onClick={() => setLinkModalOpen(true)}
      >
        <span aria-hidden="true">🔗</span>
      </Button>

      <Modal open={linkModalOpen} onClose={() => setLinkModalOpen(false)} title={t('editor:toolbar.link')} size="sm">
        <Input
          label={t('editor:toolbar.linkPrompt')}
          type="url"
          value={linkUrl}
          onChange={(event) => setLinkUrl(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') confirmLink();
          }}
        />
        <Button variant="primary" onClick={confirmLink} disabled={linkUrl.trim().length === 0}>
          {t('editor:toolbar.link')}
        </Button>
      </Modal>
    </div>
  );
}
